import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Dialog } from "component/shared/dialog";
import { styles } from "../../style";
import { getLeadsById, getMarketsOptimized } from "redux/selector";
import { Market } from "model/market";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { MarketReportSubscription, NewMarketReportSubscription } from "model/market-report-subscription";
import { MarketReportType } from "type/market-report-type";
import { updateMarketSubscriptionsNoLoading as updateMarketSubscriptions } from "redux/slice/market-report-subscriptions";
import * as env from "shared/env";

interface Props extends WithStyles<typeof styles> {
	onClose: Function;
	open?: boolean;
	leadIds: number[];
}

const Component: React.FC<Props> = ({ classes = {}, onClose, open = true, leadIds }) => {
	// Redux hooks
	const dispatch = useAppThunkDispatch();
	const markets = useSelector(getMarketsOptimized);
	const leads = useSelector((state: RootState) => getLeadsById(state, leadIds));

	// Local state
	const [marketId, setMarketId] = useState<number | undefined>(undefined);
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [listingChecked, setListingChecked] = useState<boolean>(true);
	const [openHomesChecked, setOpenHomesChecked] = useState<boolean>(true);
	const [marketChecked, setMarketChecked] = useState<boolean>(true);
	const [input, setInput] = useState<string>("");

	// Reset state when dialog opens
	useEffect(() => {
		if (open) {
			setListingChecked(true);
			setOpenHomesChecked(true);
			setMarketChecked(true);
		}
	}, [open]);

	// Memoized selected market
	const selectedMarket = useMemo(() => {
		return markets.find(market => market.id === marketId);
	}, [markets, marketId]);

	// Save function
	const save = useCallback(async (subscriptions: (MarketReportSubscription | NewMarketReportSubscription)[]) => {
		if (selectedMarket) {
			setSaving(true);
			await dispatch(updateMarketSubscriptions({ market: selectedMarket, subscriptions }));
			setSaving(false);
		}
	}, [selectedMarket, dispatch]);

	// Add function
	const add = useCallback(async () => {
		if (!selectedMarket) return;

		// Start with an empty subscriptions array - we'll only add/update subscriptions for the selected leads
		let subscriptions: (MarketReportSubscription | NewMarketReportSubscription)[] = [];

		// Get existing subscriptions for the selected leads only
		const selectedLeadIds = new Set(leads.map(l => l.id));
		const existingSubscriptions = selectedMarket.marketReportSubscriptions
			? selectedMarket.marketReportSubscriptions.filter(sub => selectedLeadIds.has(sub.lead.id))
			: [];

		// Capture report references once to ensure consistent marketReportId for all leads
		const listingReport = selectedMarket.listingReport;
		const openHomesReport = selectedMarket.openHomesReport;
		const marketReport = selectedMarket.marketReport;

		for (const selected of leads) {
			const leadId = selected.id as number;
			const lead = leads.find(lead => leadId && lead.id === leadId);

			if (!lead) continue;

			// Handle listing report
			{
				const existingSub = existingSubscriptions.find((item) =>
					item.lead.id === lead.id && item.report.type.listing
				);
				if (existingSub) {
					subscriptions.push({
						...existingSub,
						inactive: !listingChecked,
						report: listingReport || existingSub.report,
					});
				} else if (listingReport && selectedMarket.listingReportSupported) {
					subscriptions.push({
						lead: lead,
						inactive: !listingChecked,
						report: listingReport,
					});
				}
			}

			// Handle open homes report
			{
				const existingSub = existingSubscriptions.find((item) =>
					item.lead.id === lead.id && item.report.type.openHomes
				);

				if (existingSub) {
					subscriptions.push({
						...existingSub,
						inactive: !openHomesChecked,
						report: openHomesReport || existingSub.report,
					});
				} else if (openHomesReport && selectedMarket.openHomesReportSupported) {
					subscriptions.push({
						lead: lead,
						inactive: !openHomesChecked,
						report: openHomesReport,
					});
				}
			}

			// Handle market report
			{
				const existingSub = existingSubscriptions.find((item) =>
					item.lead.id === lead.id && item.report.type.market
				);
				if (existingSub) {
					subscriptions.push({
						...existingSub,
						inactive: !marketChecked,
						report: marketReport || existingSub.report,
					});
				} else if (marketReport && selectedMarket.marketReportSupported) {
					subscriptions.push({
						lead: lead,
						inactive: !marketChecked,
						report: marketReport,
					});
				}
			}
		}

		await save(subscriptions);
	}, [leads, listingChecked, openHomesChecked, marketChecked, selectedMarket, save]);

	// Event handlers
	const handleClose = useCallback(() => {
		onClose();
		setMarketId(undefined);
		setSubmitted(false);
		setInput("");
	}, [onClose]);

	const handleSelectMarket = useCallback((selectedMarketId: number) => {
		setMarketId(selectedMarketId);
	}, []);

	const marketOptions = markets.map((market) => ({
		value: market.id,
		label: market.name,
	}));

	const handleListingCheckedChange = useCallback(() => {
		setListingChecked(!listingChecked);
	}, [listingChecked]);

	const handleOpenHomesCheckedChange = useCallback(() => {
		setOpenHomesChecked(!openHomesChecked);
	}, [openHomesChecked]);

	const handleMarketCheckedChange = useCallback(() => {
		setMarketChecked(!marketChecked);
	}, [marketChecked]);

	const handleSave = useCallback(async () => {
		if (selectedMarket && marketId) {
			setSubmitted(true);
			await add();
			handleClose();
		}
	}, [selectedMarket, marketId, add, handleClose]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth={true}
			maxWidth="xs"
		>
			<Mui.DialogTitle>Add to Market</Mui.DialogTitle>
			<Mui.DialogContent dividers className={classes.dialogContent}>
				<Mui.Grid container alignItems="center" spacing={1}>
					<Mui.Grid item xs={12}>
						<Mui.DialogContentText className={classes.dialogText}>
							Add all selected leads to a Market.
						</Mui.DialogContentText>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<CustomAutocomplete
							options={marketOptions}
							textInputHeight={true}
							label="Markets"
							optionLabelExtractor={(option) => option.label}
							fullWidth
							margin="dense"
							placeholder="Select a market"
							onInputChange={(event, value, reason) => {
								if (reason === "reset") {
									setInput("");
								} else {
									setInput(value);
								}
							}}
							onChange={(event, option) => {
								if (option && option.value) {
									handleSelectMarket(option.value as number);
									setInput(option.label);
								}
							}}
							inputValue={input}
							helperText={submitted && !marketId ? "Please select a market." : undefined}
						/>
					</Mui.Grid>
					<Mui.Grid>
						{selectedMarket && (
							<div>
								{selectedMarket.listingReport &&
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												checked={listingChecked}
												onChange={handleListingCheckedChange}
												color="primary"
											/>
										}
										label={selectedMarket.listingReport.type.label}
									/>
								}
								{selectedMarket.openHomesReport &&
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												checked={openHomesChecked}
												onChange={handleOpenHomesCheckedChange}
												color="primary"
											/>
										}
										label={selectedMarket.openHomesReport.type.label}
									/>
								}
								{selectedMarket.marketReport &&
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												checked={marketChecked}
												onChange={handleMarketCheckedChange}
												color="primary"
											/>
										}
										label={selectedMarket.marketReport.type.label}
									/>
								}
							</div>
						)}
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.dialogActions}>
				<Mui.Button 
					onClick={handleClose}
					variant="outlined"
					color={"primary"}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					onClick={handleSave}
					variant="contained"
					color={"primary"}
					disabled={submitted && !marketId}
				>
					Save
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const BulkAddToMarketDialog = withStyles(Component, styles);